/** Emissor de eventos tipado e mínimo (sem dependência de framework). */
export class Emitter<Events extends Record<string, unknown>> {
  private listeners = new Map<keyof Events, Set<(payload: never) => void>>();
  private last = new Map<keyof Events, unknown>();

  on<K extends keyof Events>(event: K, fn: (payload: Events[K]) => void): () => void {
    const set = this.listeners.get(event) ?? new Set();
    set.add(fn as (payload: never) => void);
    this.listeners.set(event, set);
    return () => set.delete(fn as (payload: never) => void);
  }

  /**
   * Último valor emitido (ou semeado com `remember`). É estável entre emissões,
   * o que permite ler o estado com `useSyncExternalStore`.
   */
  latest<K extends keyof Events>(event: K): Events[K] | undefined {
    return this.last.get(event) as Events[K] | undefined;
  }

  /** Guarda o valor atual de um evento sem notificar ninguém (estado inicial). */
  protected remember<K extends keyof Events>(event: K, payload: Events[K]) {
    this.last.set(event, payload);
  }

  protected emit<K extends keyof Events>(event: K, payload: Events[K]) {
    this.last.set(event, payload);
    for (const fn of this.listeners.get(event) ?? []) (fn as (p: Events[K]) => void)(payload);
  }

  protected clearListeners() {
    this.listeners.clear();
  }
}
